"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import {
  Activity,
  ClipboardCheck,
  Gauge,
  RefreshCw,
  ScanSearch,
  ShieldAlert,
  Siren,
  Timer,
} from "lucide-react";
import { PageContainer, ConsultationCTA } from "@/components/common";
import StructuredData from "@/components/common/StructuredData";
import ServiceFaq from "@/components/services/ServiceFaq";
import { CTAButton } from "@/components/ui/cta-button";
import { renderAccent } from "@/lib/render-accent";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface LocaleProps {
  readonly locale: Locale;
}

const STATS = [
  { value: "300+", label: "Production incidents handled" },
  { value: "30+", label: "Resilience assessments run" },
  { value: "50+", label: "Load-test audits — verifying other firms' work" },
];

const ASSESS = [
  {
    icon: Gauge,
    title: "SLOs & error budgets",
    body: "We set real service-level objectives for your critical journeys and measure against them — not vanity uptime, the numbers that decide whether a customer is served.",
  },
  {
    icon: ShieldAlert,
    title: "Failure-mode & chaos testing",
    body: "We inject the failures that actually happen — a dependency down, a region out, a cloud provider having a bad hour — and watch whether the system degrades gracefully or falls over.",
  },
  {
    icon: Activity,
    title: "Load-to-failure & capacity",
    body: "We push the system to its breaking point on purpose, so you know the real ceiling and the failure behaviour — before a campaign or a market open finds it for you.",
  },
  {
    icon: RefreshCw,
    title: "DR & recovery (MTTR)",
    body: "We test the recovery, not just the backup: can you actually fail over, and how long does it really take? Most DR plans have never been run end-to-end.",
  },
  {
    icon: Siren,
    title: "Observability & incident readiness",
    body: "Would you even know? We check whether the signals, alerts, runbooks and on-call can detect and recover a real incident — the difference between minutes and days.",
  },
];

const enterpriseResilienceFaqs = [
  {
    question: "How is this different from a Big-4 or risk-advisory resilience review?",
    answer:
      "Those deliver governance and BCM advisory — frameworks, checklists, board reports. Valuable, but they don't engineer or test anything. We do the hands-on work: chaos experiments, load-to-failure, DR fail-over, SLO engineering — and produce evidence the systems actually survive, not a slide deck saying they should. We map that evidence to the same controls, so it satisfies the regulator and the engineer.",
  },
  {
    question: "You audit whether our test firm did the testing right — what does that mean?",
    answer:
      "Plenty of teams pay for a load test or a resilience exercise and get a green report — but was the load realistic, were the right scenarios covered, was the analysis sound, were the conclusions real? Having run 50+ of these audits, we independently review your (or your vendor's) testing and tell you plainly whether it was rigorous or theatre. Under BNM RMiT and MAS, the regulator increasingly wants evidence the testing was real — this is that evidence.",
  },
  {
    question: "Does this map to BNM RMiT and MAS requirements?",
    answer:
      "Yes. BNM's RMiT (updated 28 Nov 2025) requires financial institutions to run regular drills and simulations to test operational resilience and incident response; MAS has equivalent technology-risk and operational-resilience expectations. We map every finding to the relevant controls so the assessment does double duty — genuine engineering assurance and regulatory evidence. Treat it as a strong starting point for your risk and compliance teams, not legal advice.",
  },
  {
    question: "We're not a regulated financial institution — is this still for us?",
    answer:
      "Yes. Any business where an outage costs real money — retail at peak, a platform under load, a critical service — benefits from knowing its true resilience before customers find the edge. The regulatory mapping is there when you need it; the engineering value stands on its own.",
  },
  {
    question: "Who runs the assessment?",
    answer:
      "A senior platform engineer who has handled 300+ production incidents, run 30+ resilience assessments and audited 50+ load-testing engagements — hands-on, not a junior with a template. That practitioner depth is the point: we've seen how systems actually fail, so we know where to push.",
  },
  {
    question: "How long does an assessment take?",
    answer:
      "A focused assessment of a critical system is typically a matter of weeks — scoping, hands-on testing, then an evidence-based report with a prioritised remediation roadmap. A broader programme or an ongoing assurance retainer runs longer; we scope it after a short discovery.",
  },
];

function Reveal({
  children,
  className = "",
  hero = false,
}: {
  children: React.ReactNode;
  className?: string;
  hero?: boolean;
}) {
  const reduce = useReducedMotion();
  // hero: moves only, opacity stays 1 so first-viewport content paints
  // before hydration (LCP).
  return (
    <motion.div
      className={className}
      initial={reduce ? false : hero ? { y: 12 } : { opacity: 0, y: 16 }}
      whileInView={hero ? { y: 0 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
    >
      {children}
    </motion.div>
  );
}

function Hero({ locale }: LocaleProps) {
  return (
    <section className="relative overflow-hidden bg-background">
      <PageContainer className="py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "Enterprise Resilience Assessment")}
            </p>
            <h1 className="font-display text-4xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
              {renderAccent(
                t(locale, "Prove your critical systems survive — before an [[outage or the regulator]] does it for you."),
              )}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Hands-on resilience engineering — chaos, load-to-failure, DR and SLOs — mapped to BNM RMiT and MAS. Not another compliance deck. And when a test firm has already run your testing, we audit whether they actually did it right.",
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CTAButton href="#engage" data-track-location="enterprise_resilience_hero">
                {t(locale, "Book a resilience assessment")}
              </CTAButton>
            </div>
          </div>
          <Reveal hero className="relative">
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <Image
                src="/images/services/enterprise-resilience-hero.jpg"
                alt={t(locale, "Enterprise resilience assessment — stress-testing critical systems to failure")}
                width={1536}
                height={1024}
                className="h-full w-full object-cover"
                priority
              />
            </div>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-3">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-primary/15 bg-card p-6 text-center">
              <div className="font-display text-3xl font-bold text-primary">{s.value}</div>
              <div className="mt-1 text-sm text-neutral-muted">{t(locale, s.label)}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-neutral-muted">
          {t(locale, "Track record of the lead platform engineer running your assessment.")}
        </p>
      </PageContainer>
    </section>
  );
}

function Problem({ locale }: LocaleProps) {
  return (
    <section className="py-20 lg:py-28">
      <PageContainer>
        <Reveal hero className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
            {t(locale, "Compliance theatre passes the audit. It doesn't survive Monday.")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-muted">
            {t(
              locale,
              "Two things pass for resilience today, and neither is real. A resilience framework nobody ever engineered or tested — a binder that looks good until production disagrees. And a load test that was 'run' but with unrealistic load, the wrong scenarios, and a green report nobody checked. BNM RMiT and MAS now want evidence, not either of those.",
            )}
          </p>
        </Reveal>
      </PageContainer>
    </section>
  );
}

function TheAudit({ locale }: LocaleProps) {
  return (
    <section className="bg-neutral-surface py-20 lg:py-28">
      <PageContainer>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              <ScanSearch className="h-4 w-4" aria-hidden />
              {t(locale, "The differentiator")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Did your test firm actually do it right?")}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "This is where we sit above the 60 load-testing shops rather than next to them. When you've already paid for load testing or a resilience exercise, we independently audit the work: was the load realistic and at the right concurrency, were the failure scenarios the ones that matter, was the analysis sound, were the conclusions honest — or was it theatre with a green tick?",
              )}
            </p>
            <p className="mt-4 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "We've run 50+ of these audits. It's the second opinion a CTO wants before trusting a report — and the evidence a regulator wants that the testing was real.",
              )}
            </p>
          </Reveal>
          <Reveal className="rounded-3xl border border-primary/15 bg-card p-8">
            <p className="text-sm font-semibold text-neutral-emphasis">
              {t(locale, "What an audit tells you")}
            </p>
            <ul className="mt-4 space-y-3 text-sm text-neutral-muted">
              {[
                "Was the load realistic — real concurrency, think-time and data, not a toy script?",
                "Did it cover the scenarios that actually break you, or only the happy path?",
                "Was the analysis sound, or did a green dashboard hide a saturated dependency?",
                "Do the conclusions hold — or would the system still fall over on the real peak?",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  {t(locale, line)}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </PageContainer>
    </section>
  );
}

function Assess({ locale }: LocaleProps) {
  return (
    <section className="py-20 lg:py-28">
      <PageContainer>
        <Reveal className="mx-auto mb-14 max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
            {t(locale, "What we assess — and prove")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-muted">
            {t(locale, "Hands-on across the five things that decide whether a system survives real load and real failure.")}
          </p>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ASSESS.map((d) => (
            <Reveal key={d.title} className="rounded-2xl border border-neutral-200/60 bg-card p-6">
              <d.icon className="h-8 w-8 text-primary" aria-hidden />
              <h3 className="mt-4 font-display text-lg font-bold text-neutral-emphasis">{t(locale, d.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, d.body)}</p>
            </Reveal>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

function Regulatory({ locale }: LocaleProps) {
  return (
    <section className="bg-neutral-surface py-20 lg:py-28">
      <PageContainer>
        <Reveal className="mx-auto max-w-3xl text-center">
          <Timer className="mx-auto h-8 w-8 text-primary" aria-hidden />
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
            {t(locale, "Mapped to BNM RMiT & MAS — evidence, not a binder")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-neutral-muted">
            {t(
              locale,
              "BNM's RMiT (updated 28 November 2025) requires regulated institutions to run regular drills and simulations to test operational resilience and incident response; MAS sets equivalent technology-risk and operational-resilience expectations. We map every finding to the relevant controls, so one engagement gives you genuine engineering assurance and the regulatory evidence to show for it. A starting point for your risk and compliance teams — not legal advice.",
            )}
          </p>
          <p className="mt-4 text-base leading-relaxed text-neutral-muted">
            {t(locale, "New to RMiT?")}{" "}
            <Link
              href={getLocalizedPathname("/compliance/bnm-rmit/", locale)}
              className="font-semibold text-primary underline underline-offset-4"
            >
              {t(locale, "Read our plain-English guide to what BNM RMiT requires")}
            </Link>
          </p>
        </Reveal>
      </PageContainer>
    </section>
  );
}

function Engage({ locale }: LocaleProps) {
  const steps = [
    { title: "Scope & discovery", body: "We agree the critical systems, the failure scenarios that matter, and the RMiT/MAS controls in play." },
    { title: "Hands-on assessment", body: "We engineer and run the tests — chaos, load-to-failure, DR fail-over — and gather the evidence." },
    { title: "Report & remediation", body: "An evidence-based resilience report, mapped to the controls, with a prioritised remediation roadmap — and an optional assurance retainer." },
  ];
  return (
    <section id="engage" className="py-20 lg:py-28">
      <PageContainer>
        <Reveal className="mx-auto mb-14 max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
            {t(locale, "How we engage")}
          </h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.title} className="rounded-2xl border border-neutral-200/60 bg-card p-6">
              <div className="font-display text-2xl font-bold text-primary">{`0${i + 1}`}</div>
              <h3 className="mt-3 font-display text-lg font-bold text-neutral-emphasis">{t(locale, s.title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-muted">{t(locale, s.body)}</p>
            </Reveal>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}

export default function EnterpriseResilience({ locale = defaultLocale }: { locale?: Locale }) {
  return (
    <>
      <StructuredData type="faq" faqs={enterpriseResilienceFaqs} />
      <Hero locale={locale} />
      <Problem locale={locale} />
      <TheAudit locale={locale} />
      <Assess locale={locale} />
      <Regulatory locale={locale} />
      <Engage locale={locale} />
      <section className="bg-neutral-surface py-20 lg:py-28">
        <PageContainer>
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-10 text-center font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Frequently asked questions")}
            </h2>
            <ServiceFaq
              items={enterpriseResilienceFaqs.map((f) => ({
                question: t(locale, f.question),
                answer: t(locale, f.answer),
              }))}
            />
          </div>
        </PageContainer>
      </section>
      <ConsultationCTA
        title={t(locale, "Is your resilience real, or on paper?")}
        description={t(
          locale,
          "Tell us your critical systems and where you're under BNM RMiT / MAS pressure, and we'll scope a hands-on resilience assessment — or audit the testing you've already had done.",
        )}
        buttonText={t(locale, "Book a resilience assessment")}
      />
    </>
  );
}
